import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import {
  AuditoriaProcessoAauditoriaForm,
  AuditoriaProcessoAauditoriaSchema,
} from "../../../schemas/Auditoria_auditoria/auditoria_processo_auditoria";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";
import { FormRangedTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_ranged_time_masked_input";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { ProcessoAuditoriasLista } from "../../../../domain/models/processo_auditorias_lista";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { OrdemCompraCentroCustoDropDown } from "../../../../../compras/domain/models/ordem_compra_centro_custo_dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";

type Props = {
  open: boolean;
  saving: boolean;
  selectedProcesso: AuditoriaProcessoAauditoriaForm | null;
  selectedProcessoAuditoria: ProcessoAuditoriasLista | null;
  IdAuditoria: number;
  centroCustoDropdown: CentroCustoDropDown[];
  onSubmit: (form: AuditoriaProcessoAauditoriaForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
  onClose: () => void;
};

export const ProcessoModal = ({
  open,
  saving,
  IdAuditoria,
  selectedProcessoAuditoria,
  selectedProcesso,
  centroCustoDropdown,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<AuditoriaProcessoAauditoriaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AuditoriaProcessoAauditoriaSchema),
  });
  const isResponsavelManual = hookForm.watch("ResponsavelCadastrado");

  useEffect(() => {
    if (open) {
      hookForm.reset({});
      if (selectedProcessoAuditoria) {
        hookForm.reset({
          DescResponsavel: selectedProcessoAuditoria.Responsavel,
          Descricao: selectedProcessoAuditoria.Processo,
          Horario: selectedProcessoAuditoria.Horario,
          IdAuditoria: selectedProcessoAuditoria.IdAuditoria,
          RequisitoNormativo: selectedProcessoAuditoria.RequisitoNormativo,
          FuncionarioNome: selectedProcessoAuditoria.FuncionarioNome,
          IdFuncionario: selectedProcessoAuditoria.IdFuncionario,
          IdProcesso: selectedProcessoAuditoria.IdProcesso,
          Local: selectedProcessoAuditoria.Local,
          Data: selectedProcessoAuditoria.Data
            ? new Date(selectedProcessoAuditoria.Data)
            : new Date(),

          ResponsavelCadastrado:
            String(
              selectedProcessoAuditoria.ResponsavelCadastrado,
            ).toLowerCase() === "false"
              ? true
              : false,
        });
      }
    } else {
      hookForm.reset({
        IdAuditoria: IdAuditoria,
        ResponsavelCadastrado: false,
      });
    }
  }, [
    hookForm,
    open,
    selectedProcesso,
    selectedProcessoAuditoria,
    IdAuditoria,
  ]);

  const { dirtyFields } = hookForm.formState;

  useEffect(() => {
    if (dirtyFields.ResponsavelCadastrado) {
      if (isResponsavelManual === true) {
        hookForm.setValue("IdFuncionario", null as any, {
          shouldValidate: false,
        });
      } else if (isResponsavelManual === false) {
        hookForm.setValue("DescResponsavel", "", { shouldValidate: false });
      }
    }
  }, [isResponsavelManual, dirtyFields.ResponsavelCadastrado, hookForm]);

  const handleSubmit = useCallback(
    (form: AuditoriaProcessoAauditoriaForm) => {
      if (!saving) {
        const formParaSalvar = {
          ...form,
          ResponsavelCadastrado: form.ResponsavelCadastrado,
        };

        onSubmit(formParaSalvar);
      }
    },
    [onSubmit, saving],
  );

  const handleChangeHistory = useCallback(() => {
    if (selectedProcessoAuditoria) {
      onOpenChangeHistory(
        selectedProcessoAuditoria.IdProcesso,
        LogTipoTabela.PROCESSO_AUDITORIA,
      );
    }
  }, [selectedProcessoAuditoria, onOpenChangeHistory]);

  const onErrors = useCallback((errors: any) => {
    console.error("Erros de validação impedindo o submit:", errors);
  }, []);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: hookForm.handleSubmit(handleSubmit, onErrors),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "42rem",
          },
        },
      }}
    >
      <DialogTitle>Programação</DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer
            title={selectedProcesso ? "Editar" : "Cadastrar"}
            arrangement="fullRow"
          >
            <div className="flex flex-col gap-4">
              <div className="w-full">
                <DefaultFormInput
                  name="Descricao"
                  label="Processo"
                  required={true}
                  rows={4}
                  multiline={true}
                  reactHookForm={hookForm}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormDatePickerInput
                  required={true}
                  name="Data"
                  label="Data"
                  reactHookForm={hookForm}
                />
                <FormRangedTimeMaskedInput
                  name="Horario"
                  label={"Horário"}
                  reactHookForm={hookForm}
                />
              </div>
              <div className="w-full">
                <DefaultFormInput
                  name="RequisitoNormativo"
                  label="Requisito Normativo"
                  rows={2}
                  multiline={true}
                  reactHookForm={hookForm}
                />
              </div>
              <div className="w-full col-span-1 lg:col-span-2">
                <DefaultFormAutocomplete
                  name="Local"
                  label="Local"
                  options={centroCustoDropdown}
                  getOptionLabel={(x) => x.title ?? ""}
                  getOptionKey={(x) => x.id}
                  reactHookForm={hookForm}
                />
              </div>

              {/* Seção Responsável */}
              <div className="flex flex-col gap-2 p-2 border border-gray-100 rounded bg-gray-50/50">
                <div className="w-full">
                  {isResponsavelManual === true ? (
                    <DefaultFormInput
                      name="DescResponsavel"
                      label="Nome do Responsável Manual"
                      reactHookForm={hookForm}
                      required={true}
                    />
                  ) : (
                    <DropDownFuncionario
                      name="IdFuncionario"
                      readOnly={saving}
                      reactHookForm={hookForm}
                      label="Selecionar Funcionário do Sistema"
                      required={true}
                    />
                  )}
                </div>
                <div className="w-full">
                  <DefaultFormCheckBox
                    name="ResponsavelCadastrado"
                    label="Preencher o responsável manualmente?"
                    reactHookForm={hookForm}
                  />
                </div>
              </div>
            </div>
          </FormContainer>
        </ModalLoading>
      </DialogContent>
      <DialogActions>
        <div className="w-full flex items-center justify-end p-4 gap-4">
          <div>
            {isNotNullOrUndefined(selectedProcessoAuditoria) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>
          <div
            className="p-2 hover:bg-gray-100 cursor-pointer rounded text-sm font-semibold text-gray-700"
            onClick={handleClose}
          >
            Cancelar
          </div>

          <div className="w-[120px]">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton type="submit" buttonColor="primary-green">
                Salvar
              </PrimaryButton>
            )}
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
